/**
 * KeyboardShortcutsOverlay — press ? to toggle, Escape to hide, or click the
 * keyboard icon. Renders a compact popover that opens UP from the bottom-right
 * corner, anchored to its own icon (not a centered modal). Only lists shortcuts
 * that are actually wired up in Workbench.tsx.
 *
 * UX notes: entrance animation grows from the bottom-right origin (spatial
 * continuity with the trigger), respects prefers-reduced-motion, moves keyboard
 * focus into the panel on open and returns it to the trigger on close, and
 * exposes proper dialog/expanded ARIA.
 *
 * The panel measures the real room above its trigger because the Workbench
 * canvas clips overflow below the header. Its header/footer remain fixed while
 * only the shortcut list scrolls.
 *
 * Each row shows: key combo + short title + one-line context hint (when useful).
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Keyboard, X } from 'lucide-react';
import { workbenchSurfacePopover } from './workbenchStyles';

/** Dispatched by Graph Legend (and other chrome) to open this overlay. */
export const OPEN_KEYBOARD_SHORTCUTS_EVENT = 'workbench:open-keyboard-shortcuts';

export function openKeyboardShortcuts(): void {
    window.dispatchEvent(new CustomEvent(OPEN_KEYBOARD_SHORTCUTS_EVENT));
}

/** True for ?, F1, or physical Slash+Shift (some layouts never emit key='?'). */
export function isKeyboardShortcutsHelpKey(e: KeyboardEvent): boolean {
    if (e.ctrlKey || e.metaKey || e.altKey) return false;
    if (e.key === 'F1' || e.key === '?') return true;
    // US QWERTY and many others: Shift+/ — some IMEs/browsers never set key='?'
    if (e.code === 'Slash' && e.shiftKey) return true;
    return false;
}

// `sep` controls how multiple keys are joined: '+' = chord (Ctrl+Z),
// 'or' = alternatives (Ctrl+Shift+Z or Ctrl+Y). Defaults to '+'.
export type Shortcut = {
    keys: string[];
    title: string;
    /** One short clause — when the shortcut applies / what it needs */
    hint?: string;
    sep?: '+' | 'or';
};

export const WORKBENCH_SHORTCUTS: { group: string; items: Shortcut[] }[] = [
    { group: 'Canvas', items: [
        { keys: ['Scroll'], title: 'Zoom in / out' },
        { keys: ['Drag'], title: 'Pan canvas' },
        { keys: ['Click node'], title: 'Select & inspect' },
        { keys: ['Right-click'], title: 'Context menu' },
    ]},
    { group: 'Analysis', items: [
        { keys: ['I'], title: 'Investigate', hint: 'Analysis → Targeted Node Intelligence' },
        { keys: ['G'], title: 'Analyze', hint: 'Analysis → Macro Graph Analysis' },
    ]},
    { group: 'File', items: [
        {
            keys: ['Ctrl', 'S'],
            title: 'Save Snapshot',
            hint: 'Persists to investigation/case; needs canvas + unsaved changes',
        },
        {
            keys: ['Ctrl', 'Alt', 'N'],
            title: 'New Investigation',
            hint: 'Clears canvas (confirms if unsaved)',
        },
    ]},
    { group: 'Tools', items: [
        { keys: ['T'], title: 'Trace', hint: 'Follow the Money — needs selected address or session' },
        { keys: ['L'], title: 'Links', hint: 'Needs 2+ addresses (selection or canvas)' },
        { keys: ['F'], title: 'Filters', hint: 'Toggle filter bar when canvas has nodes' },
    ]},
    { group: 'History', items: [
        { keys: ['Ctrl', 'Z'], title: 'Undo' },
        { keys: ['Ctrl+Shift+Z', 'Ctrl+Y'], title: 'Redo', sep: 'or' },
    ]},
    { group: 'General', items: [
        { keys: ['?', 'F1'], title: 'This help', sep: 'or' },
        { keys: ['Esc'], title: 'Close panel / dismiss' },
        { keys: ['Ctrl', 'Shift', 'D'], title: 'Toggle debug mode' },
    ]},
];

function KeyCombo({ keys, sep = '+' }: { keys: string[]; sep?: '+' | 'or' }) {
    return (
        <div className="flex items-center gap-0.5 shrink-0">
            {keys.map((k, i) => (
                <React.Fragment key={`${k}-${i}`}>
                    {i > 0 && (
                        <span className="text-[10px] text-muted-foreground mx-0.5">
                            {sep === 'or' ? 'or' : '+'}
                        </span>
                    )}
                    <kbd className="px-1.5 py-0.5 bg-secondary border border-border rounded text-[11px] font-mono text-foreground min-w-[24px] text-center">
                        {k}
                    </kbd>
                </React.Fragment>
            ))}
        </div>
    );
}

const PANEL_TOP_GUTTER = 16;

/** Return the top edge of the nearest ancestor that can clip the popover. */
function clippingCeiling(el: HTMLElement): number {
    for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (/(auto|hidden|scroll|clip)/.test(`${style.overflow} ${style.overflowY}`)) {
            return Math.max(0, node.getBoundingClientRect().top);
        }
    }
    return 0;
}

export const KeyboardShortcutsOverlay: React.FC = () => {
    const [open, setOpen] = useState(false);
    // Drives the enter transition: mount the panel closed, then flip to `entered`
    // on the next frame so the CSS transition runs. Reset when closing.
    const [entered, setEntered] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const closeBtnRef = useRef<HTMLButtonElement>(null);
    // Avoid stealing focus on initial mount (open=false) — only restore when
    // the user actually closed the panel.
    const wasOpenRef = useRef(false);
    // Prefer returning focus to wherever the user was (e.g. Omnibar) rather
    // than always the tray button.
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const [maxHeight, setMaxHeight] = useState<number | null>(null);

    const measurePanelHeight = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const available =
            trigger.getBoundingClientRect().top - clippingCeiling(trigger) - PANEL_TOP_GUTTER;
        setMaxHeight(Math.max(0, Math.round(available)));
    }, []);

    useLayoutEffect(() => {
        if (!open) return;
        measurePanelHeight();
        window.addEventListener('resize', measurePanelHeight);
        return () => window.removeEventListener('resize', measurePanelHeight);
    }, [open, measurePanelHeight]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            // Help keys work even when Omnibar / other inputs are focused —
            // otherwise "Press ?" is a dead end after any search.
            if (isKeyboardShortcutsHelpKey(e)) {
                e.preventDefault();
                if (e.repeat) return;
                setOpen(prev => !prev);
                return;
            }

            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if ((e.target as HTMLElement)?.isContentEditable) return;

            if (e.key === 'Escape') {
                setOpen(false);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    useEffect(() => {
        const onOpenRequest = () => setOpen(true);
        window.addEventListener(OPEN_KEYBOARD_SHORTCUTS_EVENT, onOpenRequest);
        return () => window.removeEventListener(OPEN_KEYBOARD_SHORTCUTS_EVENT, onOpenRequest);
    }, []);

    // Entrance transition + focus management. On open, flip `entered` next frame
    // and move focus into the panel; on close, restore prior focus (or trigger).
    useEffect(() => {
        if (open) {
            wasOpenRef.current = true;
            const active = document.activeElement;
            previouslyFocusedRef.current =
                active instanceof HTMLElement &&
                active !== triggerRef.current &&
                active !== closeBtnRef.current
                    ? active
                    : null;
            const raf = requestAnimationFrame(() => {
                setEntered(true);
                closeBtnRef.current?.focus({ preventScroll: true });
            });
            return () => cancelAnimationFrame(raf);
        }
        setEntered(false);
        if (wasOpenRef.current) {
            wasOpenRef.current = false;
            const prev = previouslyFocusedRef.current;
            previouslyFocusedRef.current = null;
            if (prev && document.contains(prev)) {
                prev.focus();
            } else {
                triggerRef.current?.focus();
            }
        }
    }, [open]);

    return (
        // Anchor: the parent positions this at bottom-2 right-2. The popover is
        // absolutely positioned relative to this wrapper so it grows UP and to
        // the left, staying pinned to the icon in the corner.
        <div className="relative">
            {/* Lightweight click-away layer — only present while open. No screen dim. */}
            {open && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setOpen(false)}
                    aria-hidden
                />
            )}

            {open && (
                <div
                    role="dialog"
                    aria-label="Keyboard shortcuts"
                    style={{ maxHeight: maxHeight ?? undefined }}
                    className={[
                        'absolute bottom-full right-0 mb-2 z-50 w-[340px] flex flex-col overflow-hidden rounded-xl',
                        maxHeight === null ? 'max-h-[70vh]' : '',
                        workbenchSurfacePopover,
                        // Grow from the trigger in the bottom-right corner.
                        'origin-bottom-right transition-[opacity,transform] duration-200 ease-out',
                        'motion-reduce:transition-none motion-reduce:transform-none',
                        entered ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-1 scale-95',
                    ].join(' ')}
                >
                    <div className="shrink-0 flex items-center justify-between px-3 py-2.5 bg-brand-ink border-b-4 border-b-primary">
                        <div className="flex items-center gap-2 min-w-0">
                            <Keyboard className="w-4 h-4 text-primary shrink-0" />
                            <div className="min-w-0">
                                <span className="block text-sm font-semibold text-white leading-tight">Keyboard Shortcuts</span>
                                <span className="block text-[10px] text-white/65">Workbench controls</span>
                            </div>
                        </div>
                        <button
                            ref={closeBtnRef}
                            onClick={() => setOpen(false)}
                            className="p-1 rounded text-white/55 hover:text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            aria-label="Close keyboard shortcuts"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3 bg-card/40 dark:bg-card/60">
                        {WORKBENCH_SHORTCUTS.map(group => (
                            <div key={group.group}>
                                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5 border-l-2 border-primary/70 pl-1.5">
                                    {group.group}
                                </div>
                                <div className="space-y-1.5">
                                    {group.items.map((item, idx) => (
                                        <div
                                            key={`${group.group}-${item.title}-${item.keys.join('+')}-${idx}`}
                                            className="flex items-start justify-between gap-3"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="text-xs font-medium text-foreground/95 leading-snug">
                                                    {item.title}
                                                </div>
                                                {item.hint && (
                                                    <div className="text-[10px] text-muted-foreground leading-snug mt-0.5">
                                                        {item.hint}
                                                    </div>
                                                )}
                                            </div>
                                            <KeyCombo keys={item.keys} sep={item.sep} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="shrink-0 px-3 py-2 border-t border-border bg-brand-cream/30 dark:bg-primary/5 text-[10px] text-muted-foreground text-center">
                        Press <kbd className="px-1.5 py-0.5 bg-secondary border border-border rounded text-[10px] font-mono text-foreground">?</kbd> or{' '}
                        <kbd className="px-1.5 py-0.5 bg-secondary border border-border rounded text-[10px] font-mono text-foreground">F1</kbd> to toggle
                    </div>
                </div>
            )}

            <button
                ref={triggerRef}
                onClick={() => setOpen(prev => !prev)}
                className={`p-1.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    open
                        ? 'bg-primary/15 text-primary border border-primary/40'
                        : 'hover:bg-muted text-muted-foreground hover:text-primary border border-transparent hover:border-border bg-card/80 backdrop-blur-md'
                }`}
                aria-haspopup="dialog"
                aria-expanded={open}
                title="Keyboard shortcuts (? or F1)"
                data-tour="canvas-shortcuts"
            >
                <Keyboard className="w-4 h-4" />
            </button>
        </div>
    );
};
